import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  CONDITION_OPERATOR_OPTIONS,
  EVENT_OPTIONS,
  NOTIFICATIONS_ROUTE,
  applyNotificationSelectionChange,
  buildSampleNotificationRules,
  createEmptyCondition,
  createEmptyNotificationForm,
  formatConditionsPreview,
  formatNotificationDateTime,
  formatTimingReferenceDetail,
  getAvailableConditionFields,
  getConditionFieldLabel,
  getConditionOperators,
  getConditionValueLabel,
  getConditionValueOptions,
  getDestinationLabel,
  getDestinationOptions,
  getDestinationRequirement,
  getEventDefinition,
  getEventIdentity,
  getEventLabel,
  getEventMeaning,
  getMethodLabel,
  getMethodOptions,
  getRecipientLabel,
  getRecipientOptions,
  getSendToLabel,
  getSendToOptions,
  getTimingLabel,
  getTimingOptions,
  getTimingReferenceOptions,
  getTypeLabel,
  getTypeOptions,
  getWordingFields,
  getWordingLimit,
  needsRepeatInterval,
  normalizeNotificationForm,
  operatorIsMulti,
} from '../../../data/notificationsConfig';
import { buildSampleLimitRules } from '../../../data/limitsThresholdsConfig';
import {
  canAddNotificationRule,
  canEditNotificationRule,
  canSaveNotificationRule,
  canViewNotificationsConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveNotificationRule,
} from '../../../data/notificationRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyNotificationForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeNotificationForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: `${getLabel(value) || value} (Unavailable)` }];
};

const NotificationRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewNotificationsConfiguration(auth);
  const canAdd = canAddNotificationRule(auth);
  const canEdit = canEditNotificationRule(auth);
  const canSave = canSaveNotificationRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;
  const limitRules = useMemo(() => buildSampleLimitRules(), []);

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleNotificationRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  const eventOptions = useMemo(
    () => EVENT_OPTIONS.map((event) => ({ value: event.value, label: event.label })),
    [],
  );
  const recipientOptions = useMemo(
    () => withCurrentOption(getRecipientOptions(formData.event), formData.recipient, getRecipientLabel),
    [formData.event, formData.recipient],
  );
  const sendToOptions = useMemo(
    () => withCurrentOption(getSendToOptions(formData.event, formData.recipient), formData.sendTo, getSendToLabel),
    [formData.event, formData.recipient, formData.sendTo],
  );
  const methodOptions = useMemo(
    () => withCurrentOption(getMethodOptions(formData.event, formData.recipient), formData.method, getMethodLabel),
    [formData.event, formData.recipient, formData.method],
  );
  const typeOptions = useMemo(
    () => withCurrentOption(getTypeOptions(formData.event), formData.type, getTypeLabel),
    [formData.event, formData.type],
  );
  const timingOptions = useMemo(
    () => withCurrentOption(getTimingOptions(formData.event), formData.timing, getTimingLabel),
    [formData.event, formData.timing],
  );
  const delayOptions = useMemo(
    () => withCurrentOption(
      getTimingReferenceOptions('initial_send_delay', limitRules, formData.initialDelay),
      formData.initialDelay,
      (value) => formatTimingReferenceDetail(value, limitRules).label,
    ),
    [limitRules, formData.initialDelay],
  );
  const maximumOptions = useMemo(
    () => withCurrentOption(
      getTimingReferenceOptions('maximum_sends', limitRules, formData.maximumSends),
      formData.maximumSends,
      (value) => formatTimingReferenceDetail(value, limitRules).label,
    ),
    [limitRules, formData.maximumSends],
  );
  const intervalOptions = useMemo(
    () => withCurrentOption(
      getTimingReferenceOptions('repeat_send_interval', limitRules, formData.repeatInterval),
      formData.repeatInterval,
      (value) => formatTimingReferenceDetail(value, limitRules).label,
    ),
    [limitRules, formData.repeatInterval],
  );
  const destinationOptions = useMemo(
    () => withCurrentOption(
      getDestinationOptions(formData.event),
      formData.destinationLink,
      (value) => getDestinationLabel(formData.event, value),
    ),
    [formData.event, formData.destinationLink],
  );
  const conditionFields = useMemo(() => getAvailableConditionFields(formData.event), [formData.event]);
  const canAddCondition = conditionFields.length > 0;
  const typeLocked = typeOptions.length === 1;
  const timingLocked = timingOptions.length === 1;
  const showThreshold = formData.timing === 'threshold_based';
  const showRepeat = showThreshold && needsRepeatInterval(formData.maximumSends, limitRules);
  const destinationRequirement = getDestinationRequirement(formData.event);
  const wordingFields = getWordingFields(formData.method);
  const eventDefinition = getEventDefinition(formData.event);
  const conditionsPreview = formatConditionsPreview(formData.conditions);

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const handleChange = (field, value) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        activation: undefined,
        duplicate: undefined,
        conflict: undefined,
        overlap: undefined,
        concurrent: undefined,
        form: undefined,
        permission: undefined,
      };
      if (field === 'conditions') {
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('condition_')) delete nextErr[key];
        });
      }
      return nextErr;
    });
    setFormData((prev) => applyNotificationSelectionChange(prev, field, value));
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, conditions: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) {
        conditions[index].values = [];
        const operators = getConditionOperators(patch.field);
        conditions[index].operator = operators.length === 1 ? operators[0].value : '';
      }
      if (patch.operator && !operatorIsMulti(patch.operator)) {
        conditions[index].values = (conditions[index].values || []).slice(0, 1);
      }
      return { ...prev, conditions };
    });
  };

  const handleSave = () => {
    if (isCreate && !canAdd) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    if (isEdit && !canEdit) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    const result = saveNotificationRule(allRules, formData, publishedBaseline || {}, {
      originalLastUpdated: resolvedSource.lastUpdated,
      limitRules,
      canSave,
    });
    if (!result.success) {
      setErrors({ ...result.errors, activation: result.summary.join(' ') });
      return;
    }
    navigate(NOTIFICATIONS_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true, emptyMessage = 'No compatible choices available.') => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    const emptyRequired = emptyCompatible && required;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`nrl-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyRequired}
            className={adminFormDetailSelectClasses(disabledExtra || emptyRequired, Boolean(errors[name]))}
          >
            <option value="">{emptyRequired ? emptyMessage : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const renderWording = (name, label) => {
    const limit = getWordingLimit(formData.event, name);
    const displayLabel = limit != null ? `${label} (${limit})` : label;
    const isTitle = /title|subject/i.test(label);
    if (isView) return <TextValue label={label} value={formData[name]} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {displayLabel} <span className="text-alertred">*</span>
        </AdminDetailFieldLabel>
        {isTitle ? (
          <input
            id={`nrl-${name}`}
            type="text"
            placeholder={`Enter ${label.toLowerCase()}`}
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors[name]))}
          />
        ) : (
          <textarea
            id={`nrl-${name}`}
            placeholder={`Enter ${label.toLowerCase()}`}
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
            className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
            rows={3}
          />
        )}
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const renderTimingReference = (name, label, options) => {
    const detail = formData[name] ? formatTimingReferenceDetail(formData[name], limitRules) : null;
    if (isView) {
      return (
        <TextValue
          label={label}
          value={detail ? `${detail.label}\n${detail.value}` : '—'}
        />
      );
    }
    return (
      <div className="space-y-1 w-full min-w-0">
        {renderSelect(name, label, options, `Select ${label.toLowerCase()}`, false, true, 'No compatible choices available.')}
        {detail ? (
          <AdminDetailFieldValue className="py-0 break-words">
            {detail.value}
          </AdminDetailFieldValue>
        ) : null}
      </div>
    );
  };

  if (!canView && isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Notification Rule Detail</AdminPageTitle>
        <AdminFormFieldError>You do not have permission to view this Rule.</AdminFormFieldError>
      </AdminLayout>
    );
  }

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Notification Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <TextValue label="Event" value={eventDefinition?.label || getEventLabel(formData.event) || '—'} />
              <TextValue label="Event identity" value={getEventIdentity(formData.event) || '—'} mono />
              <div className="sm:col-span-2">
                <TextValue label="Source context" value={getEventMeaning(formData.event) || '—'} />
              </div>
              <TextValue label="Recipient Type" value={getRecipientLabel(formData.recipient)} />
              <TextValue label="Recipient" value={getSendToLabel(formData.sendTo)} />
              <TextValue label="Method" value={getMethodLabel(formData.method)} />
              <TextValue label="Required / Optional" value={getTypeLabel(formData.type)} />
              <TextValue label="Timing" value={getTimingLabel(formData.timing)} />
              {showThreshold ? (
                <>
                  {renderTimingReference('initialDelay', 'Initial Delay', delayOptions)}
                  {renderTimingReference('maximumSends', 'Maximum Sends', maximumOptions)}
                  {showRepeat
                    ? renderTimingReference('repeatInterval', 'Repeat Interval', intervalOptions)
                    : <TextValue label="Repeat Interval" value="Not Applicable" />}
                </>
              ) : null}
              {wordingFields.map((field) => (
                <div key={field.key} className="sm:col-span-2">
                  <TextValue label={field.label} value={formData[field.key]} />
                </div>
              ))}
              <TextValue
                label="Destination requirement"
                value={
                  destinationRequirement === 'required' ? 'Required'
                    : destinationRequirement === 'optional' ? 'Optional'
                      : destinationRequirement === 'not_available' ? 'Not Available'
                        : '—'
                }
              />
              <TextValue
                label="Destination Link"
                value={
                  destinationRequirement === 'not_available'
                    ? 'Not Available'
                    : (getDestinationLabel(formData.event, formData.destinationLink) || 'Not selected')
                }
              />
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : 'No Conditions'}
                />
              </div>
              <TextValue label="Created On" value={formatNotificationDateTime(formData.dateAdded)} />
              <TextValue label="Created By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatNotificationDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  const hasValidationErrors = Boolean(
    errors.permission
    || errors.checksUnavailable
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.concurrent
    || errors.form
    || errors.activation
    || errors.conditions,
  );

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Notification Rule' : 'Edit Notification Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="nrl-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
        </div>

        {renderSelect('event', 'Event', withCurrentOption(eventOptions, formData.event, getEventLabel), 'Select event')}
        {formData.event ? (
          <AdminConfigLockedField
            label="Source context"
            value={getEventMeaning(formData.event) || '—'}
            isView={false}
          />
        ) : null}
        {renderSelect(
          'recipient',
          'Recipient Type',
          recipientOptions,
          formData.event ? 'Select recipient type' : 'Select event first',
          !formData.event,
        )}
        {renderSelect(
          'sendTo',
          'Recipient',
          sendToOptions,
          formData.recipient ? 'Select recipient' : 'Select recipient type first',
          !formData.recipient,
        )}
        {renderSelect(
          'method',
          'Method',
          methodOptions,
          formData.recipient ? 'Select method' : 'Select recipient type first',
          !formData.recipient,
        )}
        {typeLocked ? (
          <AdminConfigLockedField label="Required / Optional" value={getTypeLabel(formData.type) || '—'} isView={false} />
        ) : (
          renderSelect(
            'type',
            'Required / Optional',
            typeOptions,
            formData.event ? 'Select Required or Optional' : 'Select event first',
            !formData.event,
          )
        )}
        {timingLocked ? (
          <AdminConfigLockedField label="Timing" value={getTimingLabel(formData.timing) || '—'} isView={false} />
        ) : (
          renderSelect(
            'timing',
            'Timing',
            timingOptions,
            formData.event ? 'Select timing' : 'Select event first',
            !formData.event,
          )
        )}
        {showThreshold ? (
          <>
            {renderTimingReference('initialDelay', 'Initial Delay', delayOptions)}
            {renderTimingReference('maximumSends', 'Maximum Sends', maximumOptions)}
            {showRepeat ? (
              renderTimingReference('repeatInterval', 'Repeat Interval', intervalOptions)
            ) : (
              <AdminConfigLockedField label="Repeat Interval" value="Not Applicable" isView={false} />
            )}
          </>
        ) : null}
        {wordingFields.map((field) => renderWording(field.key, field.label))}
        {destinationRequirement === 'not_available' ? (
          <AdminConfigLockedField label="Destination Link" value="Not Available" isView={false} />
        ) : destinationRequirement ? (
          renderSelect(
            'destinationLink',
            'Destination Link',
            destinationOptions,
            destinationRequirement === 'required' ? 'Select destination' : 'Not selected',
            false,
            destinationRequirement === 'required',
            'No compatible destinations available.',
          )
        ) : null}

        <div className="space-y-3 w-full min-w-0">
          <AdminDetailFieldLabel>Optional Conditions</AdminDetailFieldLabel>
          {(formData.conditions || []).length ? (
            <div className="space-y-3">
              {(formData.conditions || []).map((condition, index) => {
                const valueOptions = withCurrentOption(
                  getConditionValueOptions(condition.field),
                  (condition.values || [])[0],
                  (value) => getConditionValueLabel(condition.field, value),
                );
                const operatorOptions = getConditionOperators(condition.field);
                return (
                  <div key={condition.id || index} className="rounded-xl border border-lightgray p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <AdminSupportingText className="mb-0 font-semibold !text-offblack">Condition</AdminSupportingText>
                      <button
                        type="button"
                        onClick={() => handleChange(
                          'conditions',
                          (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index),
                        )}
                        className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                        aria-label="Remove Condition"
                      >
                        <i className="fi fi-rs-trash text-sm" aria-hidden />
                      </button>
                    </div>
                    {errors[`condition_${index}`] ? (
                      <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError>
                    ) : null}
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.field || ''}
                          onChange={(e) => updateCondition(index, { field: e.target.value })}
                          className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                        >
                          <option value="">Select field</option>
                          {withCurrentOption(conditionFields, condition.field, getConditionFieldLabel).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.operator || ''}
                          onChange={(e) => updateCondition(index, { operator: e.target.value })}
                          disabled={!condition.field || operatorOptions.length === 1}
                          className={adminFormDetailSelectClasses(!condition.field || operatorOptions.length === 1)}
                        >
                          <option value="">Select operator</option>
                          {(operatorOptions.length ? operatorOptions : CONDITION_OPERATOR_OPTIONS).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={(condition.values || [])[0] || ''}
                          onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                          disabled={!condition.field || !valueOptions.length}
                          className={adminFormDetailSelectClasses(!condition.field || !valueOptions.length)}
                        >
                          <option value="">{valueOptions.length ? 'Select value' : 'No compatible values available.'}</option>
                          {valueOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <AdminDetailFieldValue className="py-2">
              {canAddCondition ? 'No Conditions added' : 'No compatible Conditions available'}
            </AdminDetailFieldValue>
          )}
          {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          {canAddCondition ? (
            <button
              type="button"
              onClick={() => handleChange('conditions', [...(formData.conditions || []), createEmptyCondition()])}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
          ) : null}
        </div>

        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          </div>
        ) : null}

        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default NotificationRuleDetail;
